import * as Select from '@radix-ui/react-select'
import { AlertErrorCircle, Calendar, Check, ChevronLeft, ChevronRight, DropdownTriangle } from '@/assets/icons'
import { DatePicker, DatePickerSlotProps, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { useState, useRef, useEffect } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { extractYearMonthFromISO, monthYearToISO, parseISOToMonthYear } from './utils/date-range-selector-utils'

const MIN_YEAR = 1776

// Slot props for the date picker (year + month)
const getDatePickerSlotProps = (error?: boolean, additionalSlotProps?: any): DatePickerSlotProps<any> => {
  return {
    ...additionalSlotProps,
    textField: {
      fullWidth: true,
      variant: 'filled' as const,
      sx: {
        fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
        // Input button styles
        '& .MuiPickersFilledInput-root': {
          height: '54px',
          backgroundColor: 'white',
          borderRadius: '0.25rem',
          border: error ? `1px solid var(--color-red-400)` : `1px solid var(--color-brand-neutral-400)`,
          '&:hover': {
            backgroundColor: 'transparent',
          },
          // Focus styles
          '&.Mui-focused': {
            borderColor: error ? 'var(--color-red-400)' : 'var(--color-brand-400)',
          },
        },
        '& .MuiIconButton-root:hover': {
          backgroundColor: 'var(--color-brand-neutral-200)',
        },
        // Remove the underline styles
        '& .MuiPickersFilledInput-underline:before': {
          display: 'none',
        },
        '& .MuiPickersFilledInput-underline:after': {
          display: 'none',
        },
      },
    },
    // Year and month picker button styles
    layout: {
      sx: {
        // Arrow button to switch between year and month picker
        '& .MuiIconButton-root.MuiIconButton-sizeSmall.MuiPickersCalendarHeader-switchViewButton': {
          border: `1px solid transparent`,
          borderRadius: '50%',
        },
        // Year picker button styles
        '& .MuiYearCalendar-button': {
          fontSize: '14px',
          color: 'var(--color-brand-neutral-900)',
          cursor: 'pointer',
          borderRadius: '20px',
          padding: '2px 4px',
          margin: '1px',
          minWidth: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `1px solid var(--color-brand-neutral-300)`,
          '&:hover': {
            backgroundColor: 'var(--color-brand-neutral-200)',
          },
          '&.Mui-selected': {
            backgroundColor: 'var(--color-brand-500)',
            color: 'white',
            '&:hover': {
              backgroundColor: 'var(--color-brand-600)',
            },
          },
        },
        // Month picker button styles
        '& .MuiMonthCalendar-button': {
          fontSize: '14px',
          color: 'var(--color-brand-neutral-900)',
          cursor: 'pointer',
          borderRadius: '20px',
          padding: '2px 4px',
          margin: '1px',
          minWidth: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: `1px solid var(--color-brand-neutral-300)`,
          '&:hover': {
            backgroundColor: 'var(--color-brand-neutral-200)',
          },
          '&.Mui-selected': {
            backgroundColor: 'var(--color-brand-500)',
            color: 'white',
            '&:hover': {
              backgroundColor: 'var(--color-brand-600)',
            },
          },
        },
      },
    },
    // Popper styles (from mui-date-picker.css)
    popper: {
      sx: {
        '& .MuiPaper-root': {
          zIndex: 1500,
          borderRadius: '8px',
          boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
          pointerEvents: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          paddingBottom: '10px',
          maxHeight: '360px',
        },
      },
    },
    // Calendar header styles
    calendarHeader: {
      sx: {
        '& .MuiPickersCalendarHeader-label': {
          fontSize: '14px',
          color: 'var(--color-brand-neutral-900)',
        },
        '& .MuiPickersArrowSwitcher-button': {
          color: 'var(--color-brand-neutral-900)',
          '&:hover': {
            backgroundColor: 'var(--color-brand-neutral-200)',
          },
        },
      },
    },
  }
}

type DateRangeSelectorProps = {
  dateLow: string | null // ISO date string
  dateHigh: string | null // ISO date string
  onChange: (dateLow: string | null, dateHigh: string | null) => void
}

// Preset date range options that will be saved to Redux state
enum DateRangeOption {
  ANY_TIME = 'any_time',
  ONE_YEAR = '1_year',
  THREE_YEARS = '3_years',
  FIVE_YEARS = '5_years',
  CUSTOM = 'custom', // Indicates custom date range was selected
}

const DATE_RANGE_LABELS = {
  [DateRangeOption.ANY_TIME]: 'Any Time',
  [DateRangeOption.ONE_YEAR]: '1 Year',
  [DateRangeOption.THREE_YEARS]: '3 Years',
  [DateRangeOption.FIVE_YEARS]: '5 Years',
  [DateRangeOption.CUSTOM]: 'Custom',
}

export function DateRangeSelector(props: DateRangeSelectorProps) {
  const { dateLow, dateHigh, onChange } = props

  // View state: which UI to show (preset list or custom date pickers)
  const [showCustomPicker, setShowCustomPicker] = useState(false)
  // Control Select open state to prevent closing on Tab when in custom picker mode
  const [selectOpen, setSelectOpen] = useState(false)
  // Track if Tab was pressed to prevent Select from closing
  const tabKeyPressedRef = useRef(false)

  const now = new Date()
  const currentYear = now.getUTCFullYear()
  const currentMonth = now.getUTCMonth() // 0-indexed
  const currentDay = now.getUTCDate()

  // Local state for custom date inputs
  const [startDate, setStartDate] = useState<Dayjs | null>(parseISOToMonthYear(dateLow))
  const [endDate, setEndDate] = useState<Dayjs | null>(parseISOToMonthYear(dateHigh) || dayjs().year(currentYear).month(currentMonth))

  // Track validation errors from MUI DatePicker
  const [startError, setStartError] = useState<string | null>(null)
  const [endError, setEndError] = useState<string | null>(null)

  const startDateMax = endDate?.isBefore(dayjs().year(currentYear).month(currentMonth)) ? endDate : dayjs().year(currentYear).month(currentMonth)

  const endDateMin = startDate ? startDate : dayjs().year(MIN_YEAR).month(0)

  // Sync local state when props change
  useEffect(() => {
    setStartDate(parseISOToMonthYear(dateLow))
    setEndDate(parseISOToMonthYear(dateHigh) || dayjs().year(currentYear).month(currentMonth))
  }, [dateLow, dateHigh, currentYear, currentMonth])

  // Determine which preset option is selected based on saved state
  const getSelectedOption = (): DateRangeOption => {
    if (dateLow === null && dateHigh === null) {
      return DateRangeOption.ANY_TIME
    }

    // Extract YYYY-MM from ISO strings for comparison
    const dateLowYearMonth = extractYearMonthFromISO(dateLow)
    const currentYearStart = `${currentYear}-01`
    const threeYearsAgoStart = `${currentYear - 2}-01`
    const fiveYearsAgoStart = `${currentYear - 4}-01`

    if (dateLowYearMonth === currentYearStart && dateHigh === null) {
      return DateRangeOption.ONE_YEAR
    }
    if (dateLowYearMonth === threeYearsAgoStart && dateHigh === null) {
      return DateRangeOption.THREE_YEARS
    }
    if (dateLowYearMonth === fiveYearsAgoStart && dateHigh === null) {
      return DateRangeOption.FIVE_YEARS
    }
    // If values don't match any preset, it's a custom range
    return DateRangeOption.CUSTOM
  }

  const selectedOption = getSelectedOption()

  // Handle preset option selection
  const handleValueChange = (value: string) => {
    const option = value as DateRangeOption

    switch (option) {
      case DateRangeOption.ANY_TIME:
        onChange(null, null)
        break
      case DateRangeOption.ONE_YEAR:
        // Start from January of current year (first day)
        onChange(new Date(Date.UTC(currentYear, 0, 1, 0, 0, 0, 0)).toISOString(), null)
        break
      case DateRangeOption.THREE_YEARS:
        // Start from January 3 years ago
        onChange(new Date(Date.UTC(currentYear - 2, 0, 1, 0, 0, 0, 0)).toISOString(), null)
        break
      case DateRangeOption.FIVE_YEARS:
        // Start from January 5 years ago
        onChange(new Date(Date.UTC(currentYear - 4, 0, 1, 0, 0, 0, 0)).toISOString(), null)
        break
    }
  }

  // Handle custom date range button click
  const handleShowCustomPicker = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Sync local state with current Redux state when opening custom picker
    setStartDate(parseISOToMonthYear(dateLow))
    setEndDate(parseISOToMonthYear(dateHigh) || dayjs().year(currentYear).month(currentMonth))
    // Clear any previous error states
    setStartError(null)
    setEndError(null)
    setShowCustomPicker(true)
  }

  // Handle date changes
  const handleStartDateChange = (newValue: Dayjs | null) => {
    setStartDate(newValue)
    // Convert to ISO string and apply change
    const fromDate = monthYearToISO(newValue, false)
    // If no endDate is selected, default to current date (today) at end of day
    const toDate = monthYearToISO(endDate, true) || new Date(Date.UTC(currentYear, currentMonth, currentDay, 23, 59, 59, 999)).toISOString()

    onChange(fromDate, toDate)
  }

  const handleEndDateChange = (newValue: Dayjs | null) => {
    setEndDate(newValue)
    // Convert to ISO string and apply change
    const fromDate = monthYearToISO(startDate, false)
    // If no newValue is selected, default to current date (today) at end of day
    const toDate = monthYearToISO(newValue, true) || new Date(Date.UTC(currentYear, currentMonth, currentDay, 23, 59, 59, 999)).toISOString()
    onChange(fromDate, toDate)
  }

  // Handle back button from custom picker
  const handleBackToPresets = () => {
    setShowCustomPicker(false)
  }

  // Handle Select open state changes - prevent closing on Tab when in custom picker mode
  const handleOpenChange = (open: boolean) => {
    // Always allow opening
    if (open) {
      setSelectOpen(true)
      return
    }

    // Don't close if Tab was pressed while in custom picker mode
    if (showCustomPicker && tabKeyPressedRef.current) {
      // Tab was used for navigation, keep Select open
      // Use setTimeout to reset the flag after the close attempt is prevented
      setTimeout(() => {
        tabKeyPressedRef.current = false
      }, 0)
      return
    }

    // Normal close - reset flag and close
    tabKeyPressedRef.current = false
    setSelectOpen(false)
  }

  // Render preset options view
  return (
    <div className="flex items-center gap-2">
      <Select.Root value={selectedOption} onValueChange={handleValueChange} open={selectOpen} onOpenChange={handleOpenChange}>
        <Select.Trigger
          className="inline-flex items-center justify-center gap-1 px-3 py-1.5 text-sm text-brand-neutral-950 font-medium hover:bg-brand-neutral-200 data-[state=open]:bg-brand-neutral-100 rounded-md border-0"
          aria-label="Date range"
        >
          <span className="text-sm text-brand-neutral-600 font-medium whitespace-nowrap">Include Date Range:</span>
          <Select.Value>
            {selectedOption === DateRangeOption.CUSTOM ? (
              // Show invalid date range message if there is an error with both dates
              startError && endError ? (
                <p className="text-red-800 font-medium whitespace-nowrap text-sm"> Invalid date range</p>
              ) : (
                <span className={`text-sm text-brand-neutral-950 font-medium whitespace-nowrap`}>
                  {/* Start date label */}
                  <span className={`text-sm font-medium whitespace-nowrap ${startError ? 'text-red-800' : 'text-brand-neutral-950'}`}>
                    {startDate?.format('MM/YYYY') ?? ''}
                  </span>
                  <span className="text-sm text-brand-neutral-950 font-medium whitespace-nowrap "> - </span>
                  {/* End date label */}
                  <span className={`text-sm font-medium whitespace-nowrap ${endError ? 'text-red-800' : 'text-brand-neutral-950'}`}>
                    {endDate?.format('MM/YYYY') ?? ''}
                  </span>
                </span>
              )
            ) : (
              <span className={`text-sm text-brand-neutral-950 font-medium whitespace-nowrap`}>{DATE_RANGE_LABELS[selectedOption]}</span>
            )}
          </Select.Value>
          <Select.Icon>
            <DropdownTriangle className="h-4 w-4" />
          </Select.Icon>
        </Select.Trigger>

        <Select.Portal>
          <Select.Content
            className="bg-white rounded-md shadow-lg border border-brand-neutral-200 z-[1000] mb-0.5"
            position="popper"
            align="start"
            side="top"
            onKeyDown={(e) => {
              // Allow Tab navigation between DatePicker inputs when custom picker is shown
              // Prevent Select from closing on Tab when in custom picker view
              if (showCustomPicker && e.key === 'Tab') {
                tabKeyPressedRef.current = true
                e.stopPropagation()
                // Don't prevent default - allow normal Tab navigation
              }
            }}
          >
            <Select.Viewport className="w-60 h-[224px]">
              {showCustomPicker ? (
                <div className="h-full min-h-[224px] pt-2">
                  <div
                    className="relative flex items-center justify-stretch text-sm text-brand-neutral-950 font-medium gap-2 px-2 pb-2 hover:text-brand-neutral-800 rounded-md cursor-pointer"
                    onClick={handleBackToPresets}
                  >
                    <ChevronLeft className="w-4 h-4 shrink-0" />
                    <div className="flex items-center justify-between w-full ">Custom Date Range</div>
                  </div>
                  {/* Visual separator */}
                  <div className="flex items-center justify-between h-px w-full bg-brand-neutral-200"></div>
                  {/* Date pickers */}
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div
                      className="flex flex-col gap-3 w-full py-3 px-2"
                      onKeyDown={(e) => {
                        // Prevent Tab from closing the Select dropdown when navigating between DatePicker inputs
                        if (e.key === 'Tab') {
                          tabKeyPressedRef.current = true
                          e.stopPropagation()
                        }
                      }}
                    >
                      <DatePicker
                        value={startDate}
                        onChange={handleStartDateChange}
                        onError={(error) => setStartError(error)}
                        minDate={dayjs().year(MIN_YEAR).month(0)}
                        maxDate={startDateMax}
                        label={<span className="text-brand-neutral-600 font-sans text-base font-normal">Starting (mm/yyyy)*</span>}
                        views={['month', 'year']}
                        format="MM/YYYY"
                        slots={{
                          openPickerIcon: (props) => {
                            // Do not pass down ownerState to a svg object as it will cause React DOM warnings
                            const { ownerState, ...iconProps } = props
                            return <Calendar {...iconProps} />
                          },
                        }}
                        slotProps={getDatePickerSlotProps(!!startError)}
                        desktopModeMediaQuery="@media (min-width: 0px)" // Force desktop popper mode on all devices
                      />
                      <DatePicker
                        value={endDate}
                        onChange={handleEndDateChange}
                        onError={(error) => setEndError(error)}
                        minDate={endDateMin}
                        maxDate={dayjs().year(currentYear).month(currentMonth)}
                        label={<span className="text-brand-neutral-600 font-sans text-base font-normal">Ending (mm/yyyy)*</span>}
                        views={['month', 'year']}
                        format="MM/YYYY"
                        slots={{
                          openPickerIcon: (props) => {
                            // Do not pass down ownerState to a svg object as it will cause React DOM warnings
                            const { ownerState, ...iconProps } = props
                            return <Calendar {...iconProps} />
                          },
                        }}
                        slotProps={getDatePickerSlotProps(!!endError)}
                        desktopModeMediaQuery="@media (min-width: 0px)" // Force desktop popper mode on all devices
                      />
                      {(startError || endError) && (
                        // Show error message if either date is invalid
                        <p className="flex items-center gap-1 text-red-800 text-xs bg-red-50 rounded-md p-2">
                          <AlertErrorCircle className="shrink-0 w-3.5 h-3.5" /> Invalid date range
                        </p>
                      )}
                    </div>
                  </LocalizationProvider>
                </div>
              ) : (
                <div className="p-2 justify-evenly flex flex-col gap-2 h-full">
                  {/* Preset options */}
                  <Select.Item
                    value={DateRangeOption.ANY_TIME}
                    className="relative flex items-center justify-between px-3 py-2 text-sm text-brand-neutral-700 font-medium rounded cursor-pointer select-none hover:bg-brand-neutral-100 focus:bg-brand-neutral-100 focus:outline-none data-[highlighted]:bg-brand-neutral-100"
                  >
                    <Select.ItemText>{DATE_RANGE_LABELS[DateRangeOption.ANY_TIME]}</Select.ItemText>
                    {selectedOption === DateRangeOption.ANY_TIME && <Check className="w-4 h-4 shrink-0" />}
                  </Select.Item>

                  <Select.Item
                    value={DateRangeOption.ONE_YEAR}
                    className="relative flex items-center justify-between px-3 py-2 text-sm text-brand-neutral-700 font-medium rounded cursor-pointer select-none hover:bg-brand-neutral-100 focus:bg-brand-neutral-100 focus:outline-none data-[highlighted]:bg-brand-neutral-100"
                  >
                    <Select.ItemText>{DATE_RANGE_LABELS[DateRangeOption.ONE_YEAR]}</Select.ItemText>
                    {selectedOption === DateRangeOption.ONE_YEAR && <Check className="w-4 h-4 shrink-0" />}
                  </Select.Item>

                  <Select.Item
                    value={DateRangeOption.THREE_YEARS}
                    className="relative flex items-center justify-between px-3 py-2 text-sm text-brand-neutral-700 font-medium rounded cursor-pointer select-none hover:bg-brand-neutral-100 focus:bg-brand-neutral-100 focus:outline-none data-[highlighted]:bg-brand-neutral-100"
                  >
                    <Select.ItemText>{DATE_RANGE_LABELS[DateRangeOption.THREE_YEARS]}</Select.ItemText>
                    {selectedOption === DateRangeOption.THREE_YEARS && <Check className="w-4 h-4 shrink-0" />}
                  </Select.Item>

                  <Select.Item
                    value={DateRangeOption.FIVE_YEARS}
                    className="relative flex items-center justify-between px-3 py-2 text-sm text-brand-neutral-700 font-medium rounded cursor-pointer select-none hover:bg-brand-neutral-100 focus:bg-brand-neutral-100 focus:outline-none data-[highlighted]:bg-brand-neutral-100"
                  >
                    <Select.ItemText>{DATE_RANGE_LABELS[DateRangeOption.FIVE_YEARS]}</Select.ItemText>
                    {selectedOption === DateRangeOption.FIVE_YEARS && <Check className="w-4 h-4 shrink-0" />}
                  </Select.Item>

                  {/* Custom date range option - triggers view change */}
                  <div
                    onClick={handleShowCustomPicker}
                    className="relative flex items-center justify-between px-3 py-2 text-sm text-brand-neutral-700 font-medium rounded cursor-pointer select-none hover:bg-brand-neutral-100 focus:bg-brand-neutral-100 focus:outline-none"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleShowCustomPicker(e as any)
                      }
                    }}
                  >
                    <span>Custom Date Range</span>
                    <ChevronRight className="w-4 h-4 shrink-0" />
                  </div>
                </div>
              )}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
    </div>
  )
}
